import { useId, useRef, type ReactNode } from "react";
import { X, type LucideIcon } from "lucide-react";
import clsx from "clsx";
import { Button, type ButtonVariant } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { useModalA11y } from "../hooks/useModalA11y.js";
import styles from "./AcDialog.module.scss";

export type AcDialogTone = "default" | "danger";
/** `sm` 420 (the spec ac-dialog width) for a text confirm; `md` 480 for a richer confirm body.*/
export type AcDialogSize = "sm" | "md";

export interface AcDialogProps {
  /** Accessible dialog title. */
  title: string;
  /** Simple text body — rendered only when `children` is absent. */
  message?: ReactNode;
  /** A richer body (e.g. a preview). Takes precedence over `message`. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** aria-label for the scrim + close ✕ (default "Close") — distinct from the footer Cancel button's text. */
  closeLabel?: string;
  onConfirm: () => void;
  /** Also the dismiss handler (scrim click / Escape / the close ✕). */
  onCancel: () => void;
  /** `danger` styles the confirm as destructive (destructive variant).*/
  tone?: AcDialogTone;
  /** While true the confirm shows a spinner and both buttons are disabled. */
  loading?: boolean;
  /** The confirm button's variant (default `primary`, ink). `accent` is the crimson brand CTA — a per-dialog
   *  call, never a default (BRAND-3). Ignored when `tone="danger"`, which owns its own red. */
  confirmVariant?: ButtonVariant;
  /** One extra ghost action at the LEFT of the footer, apart from Cancel + Confirm on the right (e.g. the
   *  push dialog's "Switch account"). Disabled while `loading`. */
  leadingAction?: { label: string; onClick: () => void };
  /** Optional leading icon on the confirm button. */
  confirmIcon?: LucideIcon;
  /** Max-width preset (see `AcDialogSize`). Default `sm`. */
  size?: AcDialogSize;
  /** Hide the top-right ✕ (a compact confirm dismisses via Cancel / scrim / Escape). */
  hideClose?: boolean;
}

/**
 * The branded confirm/alert dialog ("branded confirm/alert/prompt, 420px, radius-2xl, destructive
 * variant, stacks over modals"). Built on the same overlay mechanics as `Modal` but at `--ac-z-dialog`, so it
 * layers ABOVE an open modal, and `useModalA11y`'s topmost-only Escape unwinds it first. The body is a text
 * `message` or arbitrary `children`; the footer is a Cancel + Confirm pair with a `danger` tone and a
 * width-stable `loading` state.
 */
export function AcDialog({
  title,
  message,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  closeLabel = "Close",
  onConfirm,
  onCancel,
  tone = "default",
  loading = false,
  confirmIcon,
  confirmVariant = "primary",
  leadingAction,
  size = "sm",
  hideClose = false,
}: AcDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const bodyId = useId();
  useModalA11y(dialogRef, onCancel);

  const hasBody = children != null || (message != null && message !== "");

  return (
    <div className={styles.backdrop}>
      <button
        type="button"
        className={styles.scrim}
        aria-label={closeLabel}
        tabIndex={-1}
        onClick={onCancel}
      />
      <div
        ref={dialogRef}
        className={styles.dialog}
        data-size={size}
        data-tone={tone}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={hasBody ? bodyId : undefined}
        tabIndex={-1}
      >
        <header className={styles.head}>
          <div className={styles.headText}>
            <h2 id={labelId} className={styles.title}>
              {title}
            </h2>
          </div>
          {!hideClose && (
            <button type="button" className={styles.close} aria-label={closeLabel} onClick={onCancel}>
              <Icon icon={X} size={20} />
            </button>
          )}
        </header>

        {hasBody && (
          <div id={bodyId} className={styles.body}>
            {children ?? <p className={styles.message}>{message}</p>}
          </div>
        )}

        <div className={styles.actions} data-dialog-foot>
          {leadingAction && (
            <Button type="button" variant="ghost" onClick={leadingAction.onClick} disabled={loading}>
              {leadingAction.label}
            </Button>
          )}
          <span className={styles.spacer} />
          <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            className={clsx(tone === "danger" && styles.dangerConfirm)}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmIcon && (
              <>
                <Icon icon={confirmIcon} size={16} />{" "}
              </>
            )}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
